import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  NotificationsFilterParams,
  NotificationType,
  NotificationsListResponse,
  SetReadNotificationParams
} from '@/types/notification'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { fetchNotifications, setReadNotifications, deleteNotification, bulkDeleteNotifications } from '@/lib/api'
import { NotificationScopeEnum, SortEnum } from '@/enums'

// Single notification state type
type NotificationStateType = {
  isLoading: boolean
  response: NotificationType | null
  error: {
    status?: number
    message: string
  } | null
}

// Notifications list state type
type NotificationsListStateType = {
  isLoading: boolean
  response: NotificationsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type NotificationsStoreState = {
  // Unread count state
  unreadCount: number
  setUnreadCount: (count: number) => void
  incrementUnreadCount: () => void
  decrementUnreadCount: () => void

  // Notifications list filters state
  filters: NotificationsFilterParams
  setFilters: (filters: Partial<NotificationsFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Notifications list state
  notificationsListState: NotificationsListStateType
  fetchNotifications: (params?: NotificationsFilterParams) => Promise<NotificationsListResponse>
  resetNotificationsList: () => void

  // Single notification state
  notificationState: NotificationStateType
  setReadNotifications: (params: SetReadNotificationParams) => Promise<NotificationType>
  deleteNotification: (notificationId: string) => Promise<void>
  resetNotification: () => void

  // Selection operations
  selectedNotificationIds: string[]
  selectNotification: (id: string) => void
  deselectNotification: (id: string) => void
  toggleNotificationSelection: (id: string) => void
  selectAllNotifications: (notifications: NotificationType[]) => void
  deselectAllNotifications: () => void
  isNotificationSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteNotifications: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: NotificationsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt',
  scopes: [NotificationScopeEnum.ADMINISTRATION]
}

const initialNotificationState: NotificationStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialNotificationsListState: NotificationsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useNotificationsStore = create<NotificationsStoreState>()(
  devtools((set, get) => ({
    unreadCount: 0,
    filters: defaultFilters,
    notificationState: initialNotificationState,
    notificationsListState: initialNotificationsListState,
    selectedNotificationIds: [],

    setUnreadCount: count => set({ unreadCount: Math.max(0, count) }),

    incrementUnreadCount: () =>
      set(state => ({
        unreadCount: state.unreadCount + 1
      })),

    decrementUnreadCount: () =>
      set(state => ({
        unreadCount: Math.max(0, state.unreadCount - 1)
      })),

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof NotificationsFilterParams]
          } else {
            updatedFilters[key as keyof NotificationsFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: NotificationsFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as NotificationsFilterParams['sortBy']
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value
            break
          default:
            filters[key as keyof NotificationsFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })

      return params
    },

    setReadNotifications: async (params: SetReadNotificationParams) => {
      set(() => ({
        notificationState: {
          ...get().notificationState,
          isLoading: true
        }
      }))

      try {
        const data = await setReadNotifications(params)

        set(() => ({
          notificationState: {
            ...get().notificationState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          notificationState: {
            ...initialNotificationState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteNotification: async (notificationId: string) => {
      set(() => ({
        notificationState: {
          ...get().notificationState,
          isLoading: true
        }
      }))

      try {
        await deleteNotification(notificationId)

        set(() => ({
          notificationState: initialNotificationState
        }))
      } catch (error: any) {
        set(() => ({
          notificationState: {
            ...initialNotificationState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetNotification: () => {
      set(() => ({
        notificationState: initialNotificationState
      }))
    },

    // Notifications list operations
    fetchNotifications: async (params?: NotificationsFilterParams) => {
      // If already loading, don't make another request
      const currentState = get().notificationsListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        notificationsListState: {
          ...initialNotificationsListState,
          isLoading: true
        }
      }))

      try {
        // Use provided params or fall back to store filters
        const filtersToUse = params || get().filters
        const data = await fetchNotifications(filtersToUse)

        set(() => ({
          notificationsListState: {
            ...initialNotificationsListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          notificationsListState: {
            ...initialNotificationsListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetNotificationsList: () => {
      set(() => ({
        notificationsListState: initialNotificationsListState
      }))
    },

    // Selection operations
    selectNotification: id => {
      set(state => {
        if (state.selectedNotificationIds.includes(id)) {
          return state
        }
        return { selectedNotificationIds: [...state.selectedNotificationIds, id] }
      })
    },

    deselectNotification: id => {
      set(state => ({
        selectedNotificationIds: state.selectedNotificationIds.filter(notificationId => notificationId !== id)
      }))
    },

    toggleNotificationSelection: id => {
      set(state => {
        if (state.selectedNotificationIds.includes(id)) {
          return {
            selectedNotificationIds: state.selectedNotificationIds.filter(notificationId => notificationId !== id)
          }
        }
        return { selectedNotificationIds: [...state.selectedNotificationIds, id] }
      })
    },

    selectAllNotifications: notifications => {
      set(() => ({
        selectedNotificationIds: notifications.map(notification => notification.id)
      }))
    },

    deselectAllNotifications: () => {
      set(() => ({ selectedNotificationIds: [] }))
    },

    isNotificationSelected: id => {
      return get().selectedNotificationIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteNotifications: async ids => {
      set(state => ({
        notificationState: {
          ...state.notificationState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteNotifications({ ids })

        set(state => ({
          notificationState: {
            ...state.notificationState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedNotificationIds: state.selectedNotificationIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          notificationState: {
            ...state.notificationState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useNotificationsStore
